/*
 * @Author: Ryan
 * @Date: 2025-12-03
 * @Description: Manual vs AI Video Translation comparison section
 */
import Image from 'next/image';
import React from 'react';

export const ComparisonSection: React.FC = () => {
  const traditionalFeatures = [
    {
      iconUrl: '/images/at-least-hour-delivery.png',
      text: 'At least 24-hour delivery',
    },
    {
      iconUrl: '/images/per-minute-for-video-translation.png',
      text: '$40–$60 per minute for video translation',
    },
    {
      iconUrl: '/images/relies-on-human-translators-hard-to-scale.png',
      text: 'Relies on human translators, hard to scale',
    },
    {
      iconUrl: '/images/quality-varies-depending-on-translator.png',
      text: 'Quality varies depending on translator',
    },
    {
      iconUrl: '/images/usually-supports-only-language-pairs.png',
      text: 'Usually supports only 1–2 language pairs',
    },
  ];

  const aiFeatures = [
    {
      iconUrl: '/images/translate-in-less-than-minutes.png',
      text: 'Translate in less than 5 minutes',
    },
    {
      iconUrl: '/images/consistent-high-quality-subtitles-and-dubbing.png',
      text: 'Consistent high-quality subtitles & dubbing',
    },
    {
      iconUrl: '/images/supports-languages-and-dialects.png',
      text: 'Supports 35+ languages and dialects',
    },
    {
      iconUrl: '/images/ai-voice-cloning-and-perfect-lip-sync.png',
      text: 'AI voice cloning & perfect lip sync',
    },
    {
      iconUrl: '/images/online-tool-accessible-anytime-anywhere.png',
      text: 'Online tool, accessible anytime, anywhere',
    },
  ];

  return (
    <div className="w-full py-12 pad:py-16 sm:py-20">
      <div className="max-w-[1190px] mx-auto px-4 pad:px-8 sm:px-[65px]">
        {/* 标题区域 */}
        <div className="text-center mb-10 pad:mb-12 sm:mb-16">
          {/* Section Title */}
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center mb-3 pad:mb-4">
            Manual Video Translation vs. AI Video Translation
          </h2>

          <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto">
            FalcoCut isn&rsquo;t just another video translator—it&rsquo;s an all-in-one AI
            platform that makes video translation faster, smarter, and more authentic.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-0">
          {/* Traditional Approach Card */}
          <div
            className="rounded-2xl sm:rounded-r-none p-6 pad:p-8 sm:m-6 sm:mr-0"
            style={{ backgroundColor: '#E1E8FF' }}
          >
            <h3 className="text-xl pad:text-2xl font-bold text-[#0F1117] mb-6">
              Traditional Approach
            </h3>
            <ul className="space-y-4">
              {traditionalFeatures.map((feature, index) => (
                <li key={index} className="flex items-center gap-1">
                  <Image
                    src={feature.iconUrl}
                    alt=""
                    width={12}
                    height={12}
                    className="flex-shrink-0"
                    objectFit="contain"
                  />
                  <span className="text-base text-[#0F1117] leading-relaxed">
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* FalcoCut AI Card */}
          <div className="rounded-2xl p-6 pad:p-8" style={{ backgroundColor: '#3366FF' }}>
            <h3 className="text-xl pad:text-2xl font-bold text-white mb-6">
              FalcoCut AI Video Translation
            </h3>
            <ul className="space-y-4">
              {aiFeatures.map((feature, index) => (
                <li key={index} className="flex items-center gap-1">
                  <Image
                    src={feature.iconUrl}
                    alt=""
                    width={12}
                    height={12}
                    className="flex-shrink-0"
                  />
                  <span className="text-base text-white leading-relaxed">
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
